import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Toast, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
import { usePortalAuth } from '../hooks/usePortalAuth';
import {
	applyFavoriteToCachedQueries,
	invalidateFavoriteQueries,
} from '../lib/catalogQueryKeys';
import * as catalogService from '../services/portalCatalogService';

interface FavoriteButtonProps {
	itemId: number;
	/** The favorite flag from the parent's data (catalog card, product page). */
	isFavorite: boolean;
	/** Size variant */
	size?: 'sm' | 'md';
}

/**
 * Star-shaped favorite toggle button.
 * Gray outline star when not favorited, filled gold star when favorited.
 * Requires auth — opens login modal if not authenticated.
 *
 * The star flips the moment it is clicked rather than waiting for the
 * parent's data to refetch, and the button itself refreshes every cached
 * view that shows the flag (catalog, product page, Favorites page), so no
 * caller has to know which queries those are.
 */
export function FavoriteButton({
	itemId,
	isFavorite,
	size = 'sm',
}: FavoriteButtonProps): JSX.Element {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const requireAuth = useAuthGate();
	const queryClient = useQueryClient();
	// What the star shows. Set optimistically on click; the parent's prop
	// only catches up once its query has refetched.
	const [shownFavorite, setShownFavorite] = useState(isFavorite);
	const [isSaving, setIsSaving] = useState(false);
	// The disabled attribute only lands on the next render, so a fast double
	// click could otherwise start a second request before it does. The ref
	// is checked synchronously in the handler.
	const savingRef = useRef(false);

	// Follow the parent's value when it changes (its refetch landed, or the
	// same button now shows a different item). Skipped mid-request so a
	// refetch that started before the save cannot flip the star back.
	useEffect(() => {
		if (!savingRef.current) {
			setShownFavorite(isFavorite);
		}
	}, [isFavorite, itemId]);

	const handleToggle = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (savingRef.current || !requireAuth() || token == null) {
				return;
			}

			const nextFavorite = !shownFavorite;

			savingRef.current = true;
			setIsSaving(true);
			setShownFavorite(nextFavorite);

			try {
				if (nextFavorite) {
					await catalogService.addFavorite(token, itemId);
				} else {
					await catalogService.removeFavorite(token, itemId);
				}

				applyFavoriteToCachedQueries(queryClient, itemId, nextFavorite);
				void invalidateFavoriteQueries(queryClient);
			} catch {
				// The save did not happen, so put the star back the way it was.
				setShownFavorite(!nextFavorite);
				Toast.show({
					type: 'error',
					text1: nextFavorite
						? t('Could not add to favorites')
						: t('Could not remove from favorites'),
					text2: t('Please try again.'),
				});
			} finally {
				savingRef.current = false;
				setIsSaving(false);
			}
		},
		[requireAuth, token, itemId, shownFavorite, queryClient, t],
	);

	const iconSize = size === 'md' ? 'text-lg' : 'text-sm';

	return (
		<button
			type="button"
			onClick={(e) => {
				void handleToggle(e);
			}}
			disabled={isSaving}
			aria-busy={isSaving}
			className={`flex items-center justify-center rounded-full transition-colors disabled:opacity-50 ${
				size === 'md' ? 'w-10 h-10' : 'w-8 h-8'
			} ${
				shownFavorite
					? 'text-warning hover:text-warning'
					: 'text-faint hover:text-warning'
			}`}
			aria-label={
				shownFavorite ? t('Remove from favorites') : t('Add to favorites')
			}
		>
			<FontAwesomeIcon
				icon={shownFavorite ? faStarSolid : faStarRegular}
				className={iconSize}
			/>
		</button>
	);
}
